import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'

import { useModal } from '@/hooks/useModal'
import { useToast } from '@/hooks/useToast'
import { api } from '@/services/api'
import { handleSetLicenses } from '@/storage/modules/licenses/reducer'
import { LicenseResponseProps } from '@/types/licensesProps'

import { Button } from '../Button'
import { Input } from '../form/Input'

interface FormLicenseProps {
  isExist?: LicenseResponseProps
}

export function FormLicense({ isExist }: FormLicenseProps) {
  const { register, handleSubmit, setValue } = useForm<LicenseResponseProps>()

  const [isLoading, setIsLoading] = useState(false)

  const dispatch = useDispatch()
  const { showToast } = useToast()
  const { closeModal } = useModal()

  const refreshLicenses = async () => {
    const response = await api
      .get('/licenses')
      .then((res) => res.data.data as LicenseResponseProps[])

    dispatch(handleSetLicenses({ licenses: response }))
  }

  const onSubmit = async (data: LicenseResponseProps) => {
    try {
      setIsLoading(true)

      if (isExist) {
        await api.patch(`/licenses/${isExist.id}`, data)

        showToast('License edited successfully', {
          type: 'success',
          theme: 'light',
        })
      } else {
        await api.post('/licenses', data)

        showToast('License added successfully', {
          type: 'success',
          theme: 'light',
        })
      }

      await refreshLicenses()
      closeModal()
    } catch (err: unknown) {
      const errMessage =
        err instanceof Error ? err.message : 'Error saving license'
      const apiMessage =
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        typeof err.response === 'object' &&
        err.response !== null &&
        'data' in err.response &&
        typeof err.response.data === 'object' &&
        err.response.data !== null &&
        'message' in err.response.data &&
        typeof err.response.data.message === 'string'
          ? err.response.data.message
          : errMessage

      showToast(`${apiMessage}`, {
        type: 'error',
        theme: 'light',
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (isExist) {
      setValue('title', isExist.title)
      setValue('url', isExist.url)
    }
  }, [isExist, setValue])

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <Input
          placeholder="Digit license title"
          label="License"
          name="title"
          register={register}
          required
        />

        <Input
          placeholder="Digit license URL"
          label="URL"
          name="url"
          register={register}
          required
        />

        <Button
          title={isExist ? 'Edit' : 'Save'}
          variant="purple"
          isLoading={isLoading}
        />
      </form>
    </div>
  )
}
